import { calculate } from './calculate.js';
import { initLegacyFaq } from './faq.js';
import { normalizeAmount } from './format.js';
import { openModal, prepareModal } from './modal.js';
import { persistPreferences, restorePreferences } from './preferences.js';
import { render, renderAmounts, emitInteraction } from './render.js';
import { createState } from './state.js';
import { schedulePairContent, scheduleAmountUrlUpdate } from './url.js';
import { initConverterHeaderCharts } from './header-charts.js';

function afterPaint(callback) {
  requestAnimationFrame(() => window.setTimeout(callback, 0));
}

function selectAsset(state, asset) {
  const other = state.selectedSide === 'base' ? state.quote : state.base;
  if (!asset?.id || !asset.slug || !asset.type || !asset.usd || !asset.toman || asset.slug === other.slug) return;
  if (state.selectedSide === 'base') state.base = asset;
  else state.quote = asset;
  state.userInteracted = true;
  calculate(state, state.lastEdited);
  render(state);
  persistPreferences(state);
  schedulePairContent(state);
}

export function initConverter(root) {
  if (root.dataset.converterReady) return;
  root.dataset.converterReady = '1';
  const state = createState(root);
  const onSelect = (asset) => selectAsset(state, asset);
  // Build the hidden, reusable shell outside the first opening interaction.
  afterPaint(() => {
    const prepare = () => { if (root.isConnected) prepareModal(state, onSelect); };
    if ('requestIdleCallback' in window) window.requestIdleCallback(prepare, { timeout: 1000 });
    else window.setTimeout(prepare, 0);
  });
  render(state);
  restorePreferences(state).then((saved) => {
    if (!saved || state.userInteracted) return;
    state.base = saved.base;
    state.quote = saved.quote;
    state.baseAmount = saved.baseAmount;
    calculate(state, 'base');
    render(state);
    schedulePairContent(state);
  });
  if (state.mode === 'convert' && state.base.type === 'fiat' && state.quote.type === 'fiat') schedulePairContent(state);

  root.addEventListener('input', (event) => {
    const input = event.target.closest('[data-av-model-name="base"], [data-av-model-name="quote"]');
    if (!input || !root.contains(input)) return;
    const startedAt = performance.now();
    const side = input.dataset.avModelName;
    state.userInteracted = true;
    const normalized = normalizeAmount(input.value);
    input.value = normalized.text;
    state.lastEdited = side;
    state[`${side}Amount`] = normalized.value;
    calculate(state, side);
    renderAmounts(state, { preserveEditedInput: true });
    persistPreferences(state);
    scheduleAmountUrlUpdate(state);
    afterPaint(() => {
      emitInteraction(root, 'input', startedAt);
    });
  });

  root.addEventListener('click', (event) => {
    const assetTrigger = event.target.closest('[data-ac-side-type-calc]');
    if (assetTrigger && root.contains(assetTrigger)) {
      const startedAt = performance.now();
      openModal(state, assetTrigger.dataset.acSideTypeCalc, onSelect);
      afterPaint(() => emitInteraction(root, 'dialog-open', startedAt));
      return;
    }
    const swap = event.target.closest('[data-ac-change]');
    if (swap && root.contains(swap)) {
      const startedAt = performance.now();
      state.userInteracted = true;
      [state.base, state.quote] = [state.quote, state.base];
      [state.baseAmount, state.quoteAmount] = [state.quoteAmount, state.baseAmount];
      state.lastEdited = state.lastEdited === 'base' ? 'quote' : 'base';
      calculate(state, state.lastEdited);
      render(state);
      persistPreferences(state);
      afterPaint(() => {
        schedulePairContent(state);
        emitInteraction(root, 'swap', startedAt);
      });
    }
  });
}

document.querySelectorAll('[data-converter-root]').forEach(initConverter);
document.querySelector('[data-converter-root]') && initConverterHeaderCharts(document.querySelector('[data-converter-root]'));
document.querySelectorAll('[data-converter-pair-content]').forEach(initLegacyFaq);
